import { inject, Injectable } from '@angular/core';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { Router } from '@angular/router';
import { ContentUtil } from '../../utils/content/content.util';
import { environment } from '../../../environments/environment';
import { ErrorPage, ErrorPageData } from './error-page-utils';
import { replaceSingleParam } from '../../pipes/replace-param/replace-param.pipe';

export interface ErrorContent {
    icon?: string;
    iconVariant?: 'success' | 'error';
    title: string;
    text: string;
    primaryButton?: {
        label: string;
        url: string;
    };
    secondaryButton?: {
        label: string;
        url: string;
    };
    link?: string;
    listLabel?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ErrorService {
    granteeContent = ContentUtil.granteeFragmentsContent;
    grantorContent = ContentUtil.grantorFragmentContent;
    mainContent = ContentUtil.content;
    errorContents: Record<string, ErrorContent> = {
        [ErrorPage.TECH_DIFF]: {
            title: this.granteeContent.errExperiencingTechnicalDifficulties.heading,
            text: this.granteeContent.errExperiencingTechnicalDifficulties.text1,
            primaryButton: {
                label: this.granteeContent.errExperiencingTechnicalDifficulties.navGoToMyDashboard,
                url: environment.DASHBOARD_URL,
            },
        },
        [ErrorPage.GRANTEE_CANT_COMPLETE_ONLINE]: {
            title: this.granteeContent.errProcessCanNotCompleteOnline.heading,
            text: this.granteeContent.errProcessCanNotCompleteOnline.text1,
            primaryButton: {
                label: this.granteeContent.errProcessCanNotCompleteOnline.navGoToMyDashboard,
                url: environment.DASHBOARD_URL,
            },
        },
        [ErrorPage.GRANTEE_PIN_EXPIRED]: {
            title: this.granteeContent.errPinExpired.heading,
            text: this.granteeContent.errPinExpired.text1,
            primaryButton: {
                label: this.granteeContent.errPinExpired.navGoToMyDashboard,
                url: environment.DASHBOARD_URL,
            },
        },
        [ErrorPage.GRANTEE_TOO_MANY_ATTEMPTS]: {
            title: this.granteeContent.frmcGranteeFlow.errTooManyUnsuccessfulAttempts.heading,
            text: this.granteeContent.frmcGranteeFlow.errTooManyUnsuccessfulAttempts.text1,
        },
        [ErrorPage.GRANTEE_HAS_PENDING_REQUEST]: {
            title: this.grantorContent.add_user.frmcSelectUserToGiveAccess.frmcAddPerson
                .errPendingAccessRequest.heading,
            text: this.grantorContent.add_user.frmcSelectUserToGiveAccess.frmcAddPerson
                .errPendingAccessRequest.text1,
            primaryButton: {
                label: this.granteeContent.frmcGranteeFlow.errTooManyUnsuccessfulAttempts
                    .navReturnToAccessPermissions,
                url: location.origin,
            },
            secondaryButton: {
                label: this.granteeContent.errPinExpired.navGoToMyDashboard,
                url: environment.DASHBOARD_URL,
            },
        },
        [ErrorPage.GRANTEE_FOREIGN_ADDRESS]: {
            title: this.granteeContent.frmcGranteeFlow.errForeignAddressOnlineProcess.heading,
            text: this.granteeContent.frmcGranteeFlow.errForeignAddressOnlineProcess.text1,
            primaryButton: {
                label: this.granteeContent.errPinExpired.navGoToMyDashboard,
                url: environment.DASHBOARD_URL,
            },
        },
        [ErrorPage.GRANTEE_LINK_EXPIRED]: {
            title: this.granteeContent.linkExpiredErrorMessage,
            text: '',
        },
        [ErrorPage.GAF_PARTIAL_SUCCESS]: {
            icon: 'warning-fill',
            iconVariant: 'error',
            title: this.mainContent.gafPartialSuccess.heading,
            text: this.mainContent.gafPartialSuccess.text1,
            primaryButton: {
                label: this.mainContent.gafPartialSuccess.navReturnToAccessPermissions,
                url: location.origin,
            },
            listLabel: this.mainContent.gafPartialSuccess.listLabel,
        },
        [ErrorPage.GAF_SUBMIT_ALL_FAILED]: {
            title: "We're unable to submit your access request",
            text: "We're sorry—something went wrong and your request to grant access wasn't completed. Please return to Access permissions to resubmit your request.",
            primaryButton: {
                label: this.granteeContent.frmcGranteeFlow.errTooManyUnsuccessfulAttempts
                    .navReturnToAccessPermissions,
                url: location.origin,
            },
        },
        [ErrorPage.ACCEPT_ACCESS_FLOW_PARTIAL_SUCCESS]: {
            icon: 'warning-fill',
            iconVariant: 'error',
            title: this.mainContent.acceptAccessFlowPartialSuccess.heading,
            text: this.mainContent.acceptAccessFlowPartialSuccess.text1,
            primaryButton: {
                label: this.mainContent.acceptAccessFlowPartialSuccess.navReturnToAccessPermissions,
                url: location.origin,
            },
            listLabel: this.mainContent.acceptAccessFlowPartialSuccess.listLabel,
        },
        [ErrorPage.ACCEPT_ACCESS_FLOW_SUBMIT_ALL_FAILED]: {
            title: this.mainContent.acceptAccessFlowSubmitAllFailed.heading,
            text: this.mainContent.acceptAccessFlowSubmitAllFailed.text1,
            primaryButton: {
                label: this.mainContent.acceptAccessFlowSubmitAllFailed
                    .navReturnToAccessPermissions,
                url: location.origin,
            },
        },
        [ErrorPage.RAF_PARTIAL_SUCCESS]: {
            icon: 'warning-fill',
            iconVariant: 'error',
            title: this.mainContent.rafPartialSuccess.heading,
            text: this.mainContent.rafPartialSuccess.text1,
            primaryButton: {
                label: this.mainContent.rafPartialSuccess.navReturnToAccessPermissions,
                url: location.origin,
            },
            listLabel: this.mainContent.rafPartialSuccess.listLabel,
        },
        [ErrorPage.RAF_SUBMIT_ALL_FAILED]: {
            title: this.mainContent.rafSubmitAllFailed.heading,
            text: this.mainContent.rafSubmitAllFailed.text1,
            primaryButton: {
                label: this.mainContent.rafSubmitAllFailed.navReturnToAccessPermissions,
                url: location.origin,
            },
        },
    };
    private router = inject(Router);

    showError(key: ErrorPage, pageData?: ErrorPageData) {
        let finalKey = key;
        if (!Object.hasOwn(this.errorContents, finalKey)) {
            finalKey = ErrorPage.TECH_DIFF;
        }
        const errorContent = this.interpolateContent(this.errorContents[finalKey], pageData);
        this.router.navigate([APPLICATION_ROUTES.GENERIC_ERROR], {
            state: {
                internal: true,
                errorContent,
                errorPage: finalKey,
                ...(pageData && { pageData }),
            },
        });
    }

    private interpolateContent(content: ErrorContent, pageData?: ErrorPageData): ErrorContent {
        const name = pageData?.granteeName ?? 'the user';
        const sub = (s: string) => replaceSingleParam(s, 'granteeName', name);
        return {
            ...content,
            text: sub(content.text),
            ...(content.listLabel !== undefined && { listLabel: sub(content.listLabel) }),
        };
    }
}
